import SpineWrapper from '@Core/Animation/SpineWrapper';
import { CurrencyUtils } from '@Core/Currencies/CurrencyUtils';
import { GameScene } from '@Core/index';
import { TextContainer } from '@Core/TextContainer/TextContainer';
import { isBoundingBoxClicked } from '@Core/Utils/BoundingBoxHelpers';
import { Game } from '@Game/Game';
import { GameStore } from '@GameStore/GameStore';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { ELayeringOrder } from '@GFTypes/LayeringOrder';
import { EBonusBuyEvents } from '@Managers/Events/BonusBuyEvents';
import { EButtonEvents } from '@Managers/Events/ButtonEvents';
import EventsCenter from '@Managers/Events/EventsCenter';
import { FederatedPointerEvent } from 'pixi.js';

export enum BONUS_BUY_BUTTON_SLOTS {
    BET_PUS = 'bonus_buy_bet_plus',
    BET_MINUS = 'bonus_buy_bet_minus',
    BONUS_PLAY = 'bonus_buy_play',
    DIALOG_CLOSE = 'bonus_buy_close',
    OPTION_ONE = 'bonus_buy_1_option',
    OPTION_TWO = 'bonus_buy_2_option',
    OPTION_THREE = 'bonus_buy_3_option'
}

export enum BONUS_BUY_TEXT_SLOTS {
    BET_PUS = 'bonus_buy_bet_placeholder',
    BET_MINUS = 'bonus_buy_price_placeholder'
}

export class BonusBuy {
    private readonly game: Game;
    private readonly gameStore: GameStore;
    private readonly scene: GameScene;
    private bonusBuySpine: SpineWrapper;
    private betText: TextContainer;
    private bonusPriceText: TextContainer;

    static readonly SPINE_KEY: string = 'bonusBuy';
    static readonly SKIN_NAME: string = 'default';

    constructor(scene: GameScene, game: Game) {
        this.game = game;
        this.gameStore = this.game.gameStore;
        this.scene = scene;
        EventsCenter.on(EBonusBuyEvents.BONUS_BUY_DIALOG_OPEN, this.onDialogOpen, this);
        this.createView();
    }

    private createView(): void {
        this.bonusBuySpine = new SpineWrapper(
            this.scene,
            {
                x: 0,
                y: 0
            },
            {
                dataKey: BonusBuy.SPINE_KEY,
                skinName: BonusBuy.SKIN_NAME
            },
            true
        );
        this.bonusBuySpine.spine.zIndex = ELayeringOrder.ABOVE_ALL;
        this.bonusBuySpine.spine.eventMode = 'static';
        this.bonusBuySpine.spine.on('pointerdown', this.onClick, this);
        this.bonusBuySpine.spine.visible = false;

        this.createTexts();
    }

    private canOpenDialog(): boolean {
        const bonusBuyData = this.gameStore.get(EGameStoreKeys.BonusBuy);
        const { bonusbuy_spin } = bonusBuyData.bonusBuyInitData;
        const bonusBuyConfig = this.gameStore.get(EGameStoreKeys.AuthenticateGameConfig).bonusBuy;

        const multiplicator = bonusBuyConfig?.[0]?.multiplicator ?? 0;
        const lowestBet = bonusbuy_spin.steps[0];
        const balance = this.gameStore.get(EGameStoreKeys.Account).balance.raw;

        return multiplicator * lowestBet <= balance;
    }

    private onDialogOpen(): void {
        if (!this.canOpenDialog()) {
            this.onDialogClose();
            return;
        }
        this.bonusBuySpine.spine.visible = true;

        const closestBet = this.getClosestBet();
        this.gameStore.set(EGameStoreKeys.BonusBuy, { isDialogOpen: true, bonusBuyBet: closestBet }, true);

        this.updateTexts();
    }

    private getClosestBet(): number {
        const bonusBuyData = this.gameStore.get(EGameStoreKeys.BonusBuy);
        const { bonusbuy_spin } = bonusBuyData.bonusBuyInitData;

        const bonusOption = bonusBuyData.bonusOption;
        const bonusBuyConfig = this.gameStore.get(EGameStoreKeys.AuthenticateGameConfig).bonusBuy;

        const multiplicator = bonusBuyConfig?.[bonusOption - 1]?.multiplicator ?? 0;

        const balance = this.scene.game.gameStore.get(EGameStoreKeys.Account).balance.raw;
        const currentBaseGameBet = this.scene.game.gameStore.get(EGameStoreKeys.BetState).value.raw;
        const availableBet = bonusbuy_spin.steps.findLast((bet) => bet <= currentBaseGameBet && bet * multiplicator <= balance);

        return availableBet ?? bonusbuy_spin.steps[0];
    }

    private readonly clickActions: Record<string, () => void> = {
        [BONUS_BUY_BUTTON_SLOTS.BET_PUS]: () => this.onBetPlus(),
        [BONUS_BUY_BUTTON_SLOTS.BET_MINUS]: () => this.onBetMinus(),
        [BONUS_BUY_BUTTON_SLOTS.BONUS_PLAY]: () => this.play(),
        [BONUS_BUY_BUTTON_SLOTS.DIALOG_CLOSE]: () => this.onDialogClose(),
        [BONUS_BUY_BUTTON_SLOTS.OPTION_ONE]: () => this.onOption(EBonusBuyEvents.OPTION_ONE_BUTTON_PRESSED, 1),
        [BONUS_BUY_BUTTON_SLOTS.OPTION_TWO]: () => this.onOption(EBonusBuyEvents.OPTION_TWO_BUTTON_PRESSED, 2),
        [BONUS_BUY_BUTTON_SLOTS.OPTION_THREE]: () => this.onOption(EBonusBuyEvents.OPTION_THREE_BUTTON_PRESSED, 3)
    };

    private onOption(event: EBonusBuyEvents, bonusOption: number): void {
        EventsCenter.emit(event);
        this.gameStore.set(EGameStoreKeys.BonusBuy, { bonusOption }, true);

        this.updateTexts();
    }

    private onClick(event: FederatedPointerEvent): void {
        const slot = Object.keys(this.clickActions).find((slot) => isBoundingBoxClicked(this.bonusBuySpine, slot, event));
        if (slot) {
            this.clickActions[slot]();
        }
    }

    private onDialogClose(): void {
        this.bonusBuySpine.spine.visible = false;
        this.gameStore.set(EGameStoreKeys.BonusBuy, { isDialogOpen: false }, true);
        EventsCenter.emit(EBonusBuyEvents.BONUS_BUY_DIALOG_CLOSE);
    }

    private changeBet(direction: 1 | -1): void {
        const { bonusBuyBet, bonusBuyInitData } = this.gameStore.get(EGameStoreKeys.BonusBuy);

        const steps = bonusBuyInitData.bonusbuy_spin.steps;
        const nextBet = steps[steps.indexOf(bonusBuyBet) + direction];

        if (nextBet === undefined) {
            return;
        }

        if (direction === 1 && !this.hasEnoughBalance(nextBet)) {
            return;
        }

        this.gameStore.set(EGameStoreKeys.BonusBuy, { bonusBuyBet: nextBet }, true);
        this.updateTexts();
    }

    private onBetPlus(): void {
        EventsCenter.emit(EBonusBuyEvents.BONUS_BET_INCREASE_BUTTON_PRESSED);
        this.changeBet(1);
    }

    private onBetMinus(): void {
        EventsCenter.emit(EBonusBuyEvents.BONUS_BET_DECREASE_BUTTON_PRESSED);
        this.changeBet(-1);
    }

    private hasEnoughBalance(bet: number): boolean {
        const { bonusOption } = this.gameStore.get(EGameStoreKeys.BonusBuy);
        const bonusBuyConfig = this.gameStore.get(EGameStoreKeys.AuthenticateGameConfig).bonusBuy;

        const multiplicator = bonusBuyConfig?.[bonusOption - 1]?.multiplicator ?? 0;
        const bonusPrice = multiplicator * bet;

        const balance = this.scene.game.gameStore.get(EGameStoreKeys.Account).balance.raw;

        return balance >= bonusPrice;
    }

    private play(): void {
        this.gameStore.set(EGameStoreKeys.BonusBuy, { isBonusBuyRound: true }, true);
        this.updateBetValue();
        this.onDialogClose();
        EventsCenter.emit(EButtonEvents.SPIN_BUTTON_PRESSED);
        EventsCenter.emit(EBonusBuyEvents.BONUS_BUY_PLAY);
    }

    private updateBetValue(): void {
        const bonusBet = this.gameStore.get(EGameStoreKeys.BonusBuy).bonusBuyBet;
        const index = this.game.betManager.getBetIndex(bonusBet);
        EventsCenter.emit(EButtonEvents.BET_INDEX_BUTTON_PRESSED, { betIndex: index });
    }

    private createTexts(): void {
        this.betText = TextContainer.fromSlot('bonus_buy_bet_placeholder', this.scene, '');
        this.bonusBuySpine.slotManager.assignObjectToSlot('bonus_buy_bet_placeholder', this.betText);
        this.betText.zIndex = this.bonusBuySpine.spine.zIndex += 1;

        this.bonusPriceText = TextContainer.fromSlot('bonus_buy_price_placeholder', this.scene, '');
        this.bonusBuySpine.slotManager.assignObjectToSlot('bonus_buy_price_placeholder', this.bonusPriceText);
        this.bonusPriceText.zIndex = this.bonusBuySpine.spine.zIndex += 1;
    }

    private updateTexts(): void {
        const currentBet = this.gameStore.get(EGameStoreKeys.BonusBuy).bonusBuyBet;
        const currentOption = this.gameStore.get(EGameStoreKeys.BonusBuy).bonusOption;
        const bonusBuyConfig = this.gameStore.get(EGameStoreKeys.AuthenticateGameConfig).bonusBuy;
        const multiplicator = bonusBuyConfig?.[currentOption - 1]?.multiplicator ?? 0;
        const currentBonusPrice = multiplicator * currentBet;

        this.betText.setText(CurrencyUtils.formatRawCurrencyValue(currentBet).toString());
        this.bonusPriceText.setText(CurrencyUtils.formatRawCurrencyValue(currentBonusPrice).toString());
    }
}
